'use client';

import { cn } from '@/lib/utils';
import { useLayoutEffect, useRef, useState, type ComponentPropsWithoutRef, type ReactNode, type Ref } from 'react';

type RootProps = ComponentPropsWithoutRef<'div'> & {
    children: ReactNode;
    ref?: Ref<HTMLDivElement>;
};

const Root = ({ className, children, ref, ...props }: RootProps) => {
    return (
        <div
            ref={ref}
            className={cn('w-full h-[40px] lg:px-12 px-0 bg-snk-white lg:bg-snk-black', className)}
            {...props}>
            {children}
        </div>
    );
};

type ContentProps = ComponentPropsWithoutRef<'nav'> & {
    listClassName?: string;
    ref?: Ref<HTMLElement>;
};

const Content = ({ className, listClassName, children, ref, ...props }: ContentProps) => {
    return (
        <nav
            ref={ref}
            className={cn('flex items-center justify-between h-full', className)}
            aria-label="Main navigation"
            {...props}>
            <ul className={cn('flex justify-between w-full h-full items-center', listClassName)}>{children}</ul>
        </nav>
    );
};

type ItemProps = ComponentPropsWithoutRef<'li'> & {
    children: ReactNode;
    desktopOnly?: boolean;
};

const Item = ({ children, className, desktopOnly = false, ...props }: ItemProps) => {
    const labelRef = useRef<HTMLLIElement>(null);
    const measuredRef = useRef(false);
    const [width, setWidth] = useState<number>();

    // Since the font-weight is bolder on hover, we want to avoid layout shifts
    // We measure the width of the list item in its normal state and set it as a fixed width on the element
    // so that when it becomes bold on hover, the element doesn't change width.
    // We only need to measure once, so we use a ref to track if we've already measured.
    useLayoutEffect(() => {
        if (measuredRef.current) return; // already measured once, no need to measure again
        if (!labelRef.current) return;
        const base = labelRef.current.getBoundingClientRect().width;
        setWidth(Math.ceil(base));
        measuredRef.current = true;
    }, []);

    return (
        <li
            ref={labelRef}
            style={width ? { width: `${width}px` } : undefined}
            className={cn(
                'h-full flex items-center text-snk-white font-light [&>button]:hover:opacity-90 [&>button]:hover:font-normal [&>a]:hover:opacity-90 [&>a]:hover:font-normal tracking-[0.01rem] relative whitespace-nowrap',
                desktopOnly && 'hidden lg:flex',
                className
            )}
            {...props}>
            {children}
        </li>
    );
};

const Nav = {
    Root,
    Content,
    Item,
} as const;

export { Nav };
